import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { BRAND, tcpLabel } from '@rydres/shared';
import { Button, Card, Confirm, Field, Input, Notice, Spinner, TextArea, fmtPhone, toE164 } from '@rydres/ui';
import { api } from '../lib/api.js';
import { passengerApi, riderChanged, riderChat, usePassenger, type RiderConversations } from '../lib/passenger.js';
import type { JoinView, PassengerDetails, PublicPage } from '../lib/types.js';

const errorText = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

export function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-xl space-y-5 px-4 pb-16 pt-6">
      {children}
      <p className="text-center text-xs text-muted">
        {BRAND.name} is operated by {BRAND.operator} · <a className="underline" href="/privacy">Privacy</a> · <a className="underline" href="/terms">Terms</a>
      </p>
    </div>
  );
}

/** Phone and a texted code; the account is the phone number. */
export function SignIn({ onDone }: { onDone: () => void }) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const e164 = toE164(phone);
    if (!e164) return setError('Enter a valid mobile number.');
    setBusy(true);
    setError(null);
    try {
      if (!sent) {
        await passengerApi.requestOtp(e164);
        setSent(true);
      } else {
        await passengerApi.verifyOtp(e164, code.trim(), 'passenger');
        riderChanged();
        onDone();
      }
    } catch (err) {
      setError(errorText(err, 'That did not work. Try again.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3">
      <Field label="Mobile number">
        <Input required inputMode="tel" autoComplete="tel" value={phone} disabled={sent} onChange={(e) => setPhone(e.target.value)} />
      </Field>
      {sent && (
        <Field label="Code we texted you">
          <Input required autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
      )}
      {error && <Notice tone="danger">{error}</Notice>}
      <Button type="submit" disabled={busy} className="w-full">{busy ? 'One moment…' : sent ? 'Sign in' : 'Text me a code'}</Button>
      <p className="text-xs text-muted">We text you a code to sign in. Message and data rates may apply. See our <a className="underline" href="/privacy">privacy policy</a> and <a className="underline" href="/terms">terms</a>.</p>
    </form>
  );
}

function DetailsForm({ initial, submitLabel, onSubmit }: { initial: PassengerDetails; submitLabel: string; onSubmit: (d: PassengerDetails) => Promise<void> }) {
  const [f, setF] = useState({ name: initial.name ?? '', email: initial.email ?? '', needs: initial.needs ?? '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await onSubmit({ name: f.name.trim() || null, email: f.email.trim() || null, needs: f.needs.trim() || null });
      setSaved(true);
    } catch (err) {
      setError(errorText(err, 'Could not save.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3">
      <Field label="Your name"><Input required autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      <Field label="Email (optional)"><Input type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
      <Field label="Anything drivers should know (optional)"><TextArea value={f.needs} onChange={(e) => setF({ ...f, needs: e.target.value })} placeholder="Child seat, wheelchair, lots of luggage…" /></Field>
      {error && <Notice tone="danger">{error}</Notice>}
      {saved && submitLabel === 'Save' && <Notice tone="ok">Saved. Drivers you share with see the change now.</Notice>}
      <Button type="submit" disabled={busy} className="w-full">{busy ? 'Saving…' : submitLabel}</Button>
    </form>
  );
}

function DriverCard({ page }: { page: Pick<PublicPage, 'displayName' | 'legalName' | 'tcpNumber' | 'photoUrl'> }) {
  return (
    <header className="flex items-center gap-4">
      {page.photoUrl ? (
        <img src={page.photoUrl} alt="" className="h-16 w-16 rounded-2xl object-cover" />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand text-2xl font-black text-on-brand">{(page.displayName ?? '?').slice(0, 1)}</div>
      )}
      <div className="min-w-0">
        <h1 className="text-2xl font-black leading-tight">{page.displayName}</h1>
        <p className="text-xs text-muted">{page.legalName} · {tcpLabel(page.tcpNumber)}</p>
      </div>
    </header>
  );
}

/** Where a sign-in may return to: a driver's page, or one of the passenger's own pages. */
const BACK = /^\/([a-z0-9-]{2,40}|me\/(services|people|account))$/i;

/**
 * What a passenger page shows before its own content: a spinner, a note while passenger accounts
 * are closed, or the sign-in form, after which a `?back=` page (a booking page) opens again.
 */
export function useRiderGate(title: string, intro: string) {
  const p = usePassenger();
  const navigate = useNavigate();
  const back = useSearchParams()[0].get('back');
  const backTo = back && BACK.test(back) ? back : null;
  const gate = p.loading ? (
    <Shell><Spinner /></Shell>
  ) : !p.open ? (
    <Shell>
      <h1 className="text-2xl font-black">{title}</h1>
      <Notice tone="info">Passenger accounts open soon. You can still book any driver from their page.</Notice>
    </Shell>
  ) : !p.me ? (
    <Shell>
      <h1 className="text-2xl font-black">{title}</h1>
      <p className="text-sm text-ink2">{intro}</p>
      <SignIn onDone={() => void p.reload().then((me) => me && backTo && navigate(backTo))} />
    </Shell>
  ) : null;
  return { p, gate };
}

/** rydres.com/me/account: the passenger's details, texts about messages, sign-out and deleting the account. */
export function AccountPage() {
  const { p, gate } = useRiderGate('Settings', `One account for every ${BRAND.name} driver you ride with. You choose which drivers see your details, and you keep them up to date in one place.`);
  const [alerts, setAlerts] = useState<RiderConversations['alerts'] | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [gone, setGone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const signedIn = Boolean(p.me);
  useEffect(() => {
    if (signedIn) riderChat.list().then((r) => setAlerts(r.alerts), () => setAlerts(null));
  }, [signedIn]);
  if (gone) return <Shell><Notice tone="ok">Your account and the details you entered are deleted.</Notice></Shell>;
  if (gate) return gate;
  const me = p.me!;
  const toggleAlerts = async (on: boolean) => {
    setError(null);
    try {
      setAlerts((await riderChat.alerts(on)).alerts);
    } catch (err) {
      setError(errorText(err, 'Could not change that.'));
    }
  };
  return (
    <Shell>
      <div>
        <h1 className="text-2xl font-black">Settings</h1>
        <p className="text-sm text-muted">{fmtPhone(me.phone)}</p>
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
      <Card title="Your details">
        <DetailsForm initial={me.details} submitLabel="Save" onSubmit={async (d) => void (await p.save(d))} />
      </Card>
      {alerts && (
        <Card title="Texts about new messages">
          <label className="flex items-start gap-2 text-sm text-ink2">
            <input id="message-alerts" type="checkbox" className="mt-1" checked={alerts.on} onChange={(e) => void toggleAlerts(e.target.checked)} />
            <span>{alerts.wording}</span>
          </label>
          {!alerts.textsLive && <p className="mt-2 text-xs text-muted">These texts start soon; until then, check People or ask your driver to call.</p>}
        </Card>
      )}
      <Button variant="ghost" className="w-full" onClick={() => void p.signOut()}>Sign out</Button>
      <Card title="Delete your account">
        <p className="text-sm text-ink2">Removes your sign-in and the details you entered, and drivers stop seeing them. Drivers keep their own records of rides you took with them.</p>
        <Button variant="danger" className="mt-3" onClick={() => setDeleting(true)}>Delete my account</Button>
      </Card>
      {deleting && (
        <Confirm
          title="Delete your account?"
          body="This cannot be undone. You can make a new account later with the same phone."
          confirmLabel="Delete"
          danger
          onCancel={() => setDeleting(false)}
          onConfirm={async () => {
            await passengerApi.call('DELETE', '/passenger/me').catch(() => undefined);
            await p.signOut();
            setDeleting(false);
            setGone(true);
          }}
        />
      )}
    </Shell>
  );
}

/** rydres.com/<handle>/join: the page a driver's QR code opens, to share details with that driver. */
export function JoinPage() {
  const { handle = '' } = useParams();
  const p = usePassenger();
  const [page, setPage] = useState<PublicPage | null | undefined>(undefined);
  const [view, setView] = useState<JoinView | null>(null);
  const [done, setDone] = useState(false);
  useEffect(() => {
    api.call<PublicPage>('GET', `/public/pages/${encodeURIComponent(handle)}`, { auth: false }).then(setPage, () => setPage(null));
  }, [handle]);
  useEffect(() => {
    if (p.me) passengerApi.call<JoinView>('GET', `/passenger/join/${encodeURIComponent(handle)}`).then(setView, () => setView(null));
  }, [p.me, handle]);

  if (page === undefined || p.loading) return <Shell><Spinner /></Shell>;
  if (page === null) return <Shell><Notice tone="warn">There is no driver page at this address.</Notice></Shell>;
  const name = page.displayName ?? 'your driver';
  const book = <Link to={`/${page.handle}`} className="block"><Button className="w-full">Book a ride with {name}</Button></Link>;
  return (
    <Shell>
      <DriverCard page={page} />
      {!p.open ? (
        <>
          <Notice tone="info">Passenger accounts open soon. You can still book {name} directly.</Notice>
          {book}
        </>
      ) : done || view?.shared ? (
        <>
          <Notice tone="ok">{name} has your details{done ? '' : ' already'}. Change them any time in your account.</Notice>
          {book}
          <Link to="/me" className="block text-center text-sm font-bold text-accent-text underline">Your account</Link>
        </>
      ) : !p.me ? (
        <>
          <p className="text-sm text-ink2">Share your name and number with {name} once. Next time they can book you in seconds, and your details stay up to date in one {BRAND.name} account that you control.</p>
          <SignIn onDone={() => void p.reload()} />
        </>
      ) : !view ? (
        <Spinner />
      ) : (
        <>
          <p className="text-sm text-ink2">Check your details and share them with {name}; you can stop sharing any time.</p>
          <DetailsForm
            initial={{ ...view.details, name: view.details.name ?? view.prefill.name, email: view.details.email ?? view.prefill.email }}
            submitLabel={`Share with ${name}`}
            onSubmit={async (d) => {
              await p.save(d);
              await p.share(page.handle, true);
              setDone(true);
            }}
          />
        </>
      )}
    </Shell>
  );
}
